import { Box, Typography, CircularProgress } from '@mui/material';
import { useParams, Link } from 'react-router-dom';
import { ExternalLinkIcon } from '@assets/svgs';
import { useQuote } from '@services/quotes/useQuote';
import { useGetPolicy } from '@services/policy/policies';
import { usePolicyTransactionsGrouped } from '../hooks/usePolicyTransactionsGrouped';
import { TermGroup } from '../components/TermGroup';
import { LoadMoreButton } from '../components/LoadMoreButton';
import { QuoteCard } from '../components/QuoteCard';
import { hasMultipleTerms } from '../utils/groupTransactionsByTerm';
import { getFormattedPolicyNumber } from '@utils/policy/getFormattedPolicyNumber';
import { externalLinkIconStyle } from '../utils/constants';

export const PolicyTab = () => {
  const { quoteId } = useParams<{ quoteId: string }>();
  const { quote } = useQuote(quoteId);
  const policyId = quote?.policyId;

  const { data: policy } = useGetPolicy({ policyId });

  const { termGroups, isLoading, hasMore, loadMore } = usePolicyTransactionsGrouped({
    policyId,
    enabled: !!policyId,
  });

  const currentTransactionId = quote?.policyTransaction?.id;

  // If no policy exists (unbound new business quote), show single quote card
  if (!policyId) {
    if (!quote) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={24} />
        </Box>
      );
    }
    return (
      <Box sx={{ pt: 0.5 }}>
        <QuoteCard quote={quote} isCurrent />
      </Box>
    );
  }

  // Get active term count for policy number display
  const termCount = policy?.activePolicyTerm?.termCount;

  const policyNumber = getFormattedPolicyNumber(
    policy?.policyNumberPrefix || '',
    policy?.policyNumber?.policyNumber || '',
    termCount,
  );

  const offering = policy?.boundQuote?.offering?.insuranceProduct?.name || '';
  const packageLabel = policy?.packageLabel || '';

  const showTermHeaders = hasMultipleTerms(termGroups);

  if (isLoading && termGroups.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }
  return (
    <Box>
      {/* Policy Header */}
      <Box sx={{ mb: 2, px: 2, pt: 2 }}>
        <Link to={`/policy/${policyId}`} target='_blank' rel='noopener noreferrer' style={{ textDecoration: 'none' }}>
          <Typography
            variant='body1'
            sx={{
              color: 'secondary.main',
              fontWeight: 700,
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            {policyNumber} <ExternalLinkIcon width='10px' height='10px' style={externalLinkIconStyle} />
          </Typography>
        </Link>
        {offering && (
          <Typography variant='caption' color='text.secondary' display='block' fontWeight='normal'>
            {offering}
          </Typography>
        )}
        {packageLabel && (
          <Typography variant='caption' color='text.secondary' display='block' fontWeight='normal'>
            {packageLabel}
          </Typography>
        )}
      </Box>

      {/* Transactions grouped by term */}
      <Box>
        {termGroups.map((termGroup) => (
          <TermGroup
            key={termGroup.termNumber}
            termGroup={termGroup}
            currentTransactionId={currentTransactionId}
            showTermHeader={showTermHeaders}
          />
        ))}
        <LoadMoreButton onClick={loadMore} isLoading={isLoading} hasMore={hasMore} />
      </Box>
    </Box>
  );
};
